import apiClient from './client'
import { Schema, SchemaLayoutItem } from '../types'

export const getSchemas = async (projectId: number, electricalPanelId?: number): Promise<Schema[]> => {
  let url = `/api/schemas?project_id=${projectId}`
  if (electricalPanelId) {
    url += `&electrical_panel_id=${electricalPanelId}`
  }
  
  const response = await apiClient.get(url)
  
  // After interceptor fix, response.data contains the actual data
  if (Array.isArray(response.data)) {
    return response.data as Schema[]
  }
  
  return []
}

export const getSchema = async (id: number): Promise<Schema> => {
  const response = await apiClient.get(`/api/schemas/${id}`)
  
  if (response.data && typeof response.data === 'object') {
    return response.data as Schema
  }
  
  throw new Error('Schema not found')
}

export const createSchema = async (schemaData: Omit<Schema, 'id' | 'created_at'>): Promise<Schema> => {
  const response = await apiClient.post('/api/schemas', schemaData)
  
  if (response.data?.id) {
    return getSchema(Number(response.data.id))
  }
  
  if ((response as any).id) {
    return getSchema(Number((response as any).id))
  }
  
  throw new Error('Failed to create schema')
}

export const updateSchema = async (id: number, schemaData: Partial<Schema>): Promise<Schema> => {
  await apiClient.put(`/api/schemas/${id}`, schemaData)
  return getSchema(id)
}

export const deleteSchema = async (id: number): Promise<void> => {
  await apiClient.delete(`/api/schemas/${id}`)
}



interface ApiResponse<T> {
  success: boolean
  data: T
  error?: string
}

// Получить layout схемы (расположение устройств)
export const getSchemaLayout = async (schemaId: number): Promise<ApiResponse<SchemaLayoutItem[]>> => {
  try {
    const response = await apiClient.get(`/api/schema_layout?schema_id=${schemaId}`) as any;
    
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: response interceptor уже извлёк данные
    // response.data теперь содержит массив напрямую (не wrapper)
    if (Array.isArray(response.data)) {
      return { success: true, data: response.data };
    }
    
    // Fallback для других форматов
    if (response && typeof response === 'object' && 'success' in response && response.success) {
      return response as ApiResponse<SchemaLayoutItem[]>;
    }
    
    if (Array.isArray(response)) {
      return { success: true, data: response };
    }
    
    if (response && typeof response === 'object' && 'success' in response && response.success === false) {
      return { success: false, data: [], error: response.error || 'Unknown error' };
    }
    
    return { success: true, data: [] };
  } catch (error) {
    return { success: false, data: [], error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

// BATCH API: Load complete schema layout with all related data in one request
export const getSchemaLayoutBatch = async (schemaId: number) => {
  try {
    const response = await apiClient.get(`/api/schemas/${schemaId}/batch-layout`) as any;
    
    // Response interceptor has already unwrapped the data
    if (response.data && typeof response.data === 'object') {
      return { success: true, data: response.data };
    }
    
    // Fallback
    if (response && typeof response === 'object' && 'success' in response) {
      return response;
    }
    
    return { success: false, data: { layout: [], contacts: [] }, error: 'Invalid response format' };
  } catch (error) {
    return { 
      success: false, 
      data: { layout: [], contacts: [] }, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Добавить устройство на схему
export const addDeviceToSchema = async (
  schemaId: number,
  deviceInstanceId: number,
  positionX: number,
  positionY: number,
  rotation: number = 0
): Promise<ApiResponse<{ id: number }>> => {
  try {
    const response = await apiClient.post('/api/schema_layout', {
      schema_id: schemaId,
      device_instance_id: deviceInstanceId,
      position_x: positionX,
      position_y: positionY,
      rotation: rotation,
      visible: true
    }) as any;
    
    return response as ApiResponse<{ id: number }>;
  } catch (error) {
    throw error;
  }
};

// Обновить позицию устройства на схеме
export const updateDevicePosition = async (
  layoutId: number,
  positionX: number,
  positionY: number,
  rotation: number = 0
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiClient.put(`/api/schema_layout/${layoutId}`, {
      position_x: positionX,
      position_y: positionY,
      rotation: rotation,
      visible: true,
      properties: null // Явно передаем null для properties
    }) as any;
    
    return response as ApiResponse<any>;
  } catch (error) {
    throw error;
  }
};

// Удалить устройство со схемы
export const removeDeviceFromSchema = async (layoutId: number): Promise<ApiResponse<any>> => {
  try {
    const response = await apiClient.delete(`/api/schema_layout/${layoutId}`) as any;
    
    // Response interceptor has already unwrapped the data
    if (response && typeof response === 'object' && 'success' in response) {
      return response as ApiResponse<any>;
    }
    
    // Fallback for other formats
    return { success: true, data: response };
  } catch (error) {
    throw error;
  }
}; 

// Обновить позицию устройства на схеме по schema_id и device_instance_id
export const updateDevicePositionByIds = async (
  schemaId: number,
  deviceInstanceId: number,
  positionX: number,
  positionY: number,
  rotation: number = 0,
  properties: any = null
): Promise<ApiResponse<any>> => {
  try {
    const response = await apiClient.put(`/api/schema_layout_by_ids`, {
      schema_id: schemaId,
      device_instance_id: deviceInstanceId,
      position_x: positionX,
      position_y: positionY,
      rotation: rotation,
      visible: true,
      properties: properties
    }) as any;
    
    return response as ApiResponse<any>;
  } catch (error) {
    throw error;
  }
}; 

// ============= NEW: SCHEMA ISOLATION FUNCTIONS =============

import { createSchemaManager, type SchemaType } from '../components/shared/SchemaManager';

/**
 * NEW: Получить layout схемы с изоляцией по schema_type
 */
export const getSchemaLayoutWithIsolation = async (
  schemaType: SchemaType, 
  schemaId: number
): Promise<ApiResponse<SchemaLayoutItem[]>> => {
  try {
    const schemaManager = createSchemaManager(schemaType, schemaId);
    const url = schemaManager.buildApiUrl('layout');
    
    const response = await apiClient.get(url) as any;
    
    // КРИТИЧЕСКОЕ ИСПРАВЛЕНИЕ: response interceptor уже извлёк данные
    if (Array.isArray(response.data)) {
      return { success: true, data: response.data };
    }
    
    // Fallback для других форматов
    if (response && typeof response === 'object' && 'success' in response && response.success) {
      return response as ApiResponse<SchemaLayoutItem[]>;
    }
    
    if (Array.isArray(response)) {
      return { success: true, data: response };
    }
    
    return { success: true, data: [] };
  } catch (error) {
    return { success: false, data: [], error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * NEW: Добавить устройство на схему с изоляцией по schema_type
 */
export const addDeviceToSchemaWithIsolation = async (
  schemaType: SchemaType,
  schemaId: number,
  deviceInstanceId: number,
  positionX: number,
  positionY: number,
  rotation: number = 0
): Promise<ApiResponse<any>> => {
  try {
    const schemaManager = createSchemaManager(schemaType, schemaId);
    const url = schemaManager.buildApiUrl('layout');
    
    const response = await apiClient.post(url, {
      schema_id: schemaId,
      schema_type: schemaType, // NEW: Изоляция
      device_instance_id: deviceInstanceId,
      position_x: positionX,
      position_y: positionY,
      rotation: rotation,
      visible: true,
      properties: null
    }) as any;
    
    return response as ApiResponse<any>;
  } catch (error) {
    throw error;
  }
};

/**
 * NEW: Обновить позицию устройства на схеме с изоляцией по schema_type
 */
export const updateDevicePositionWithIsolation = async (
  schemaType: SchemaType,
  schemaId: number,
  deviceInstanceId: number,
  positionX: number,
  positionY: number,
  rotation: number = 0,
  properties: any = null
): Promise<ApiResponse<any>> => {
  try {
    // NEW: Используем прямой API endpoint с schema_type изоляцией
    const response = await apiClient.put(`/api/schema_layout_by_ids`, {
      schema_id: schemaId,
      schema_type: schemaType, // NEW: Изоляция
      device_instance_id: deviceInstanceId,
      position_x: positionX,
      position_y: positionY,
      rotation: rotation,
      visible: true,
      properties: properties
    }) as any;
    
    return response as ApiResponse<any>;
  } catch (error) {
    throw error;
  }
}; 